import {
  Button,
  FormControlSizes,
  HStack,
  Icon,
  IconButton,
  IconName,
  NumberInput,
  Tooltip,
  type NumberInputProps,
  type NumericField,
} from '@talos/kyoko';
import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

type PricingNumberInputProps = {
  field: NumericField;
  showIncrementDecrementButtons?: boolean;
  incrementDecrementButtonsDisabled?: boolean;
  onIncrement?: () => void;
  onDecrement?: () => void;
  onZero?: () => void;
  tooltip?: string;
  iconVariant?: ComponentProps<typeof Icon>['variant'];
  invalid?: boolean;
} & Omit<NumberInputProps, 'value' | 'disabled' | 'invalid' | 'prefix' | 'variant'>;

export const PricingNumberInput = ({
  field,
  showIncrementDecrementButtons = false,
  incrementDecrementButtonsDisabled = false,
  onIncrement,
  onDecrement,
  onZero,
  tooltip,
  iconVariant,
  invalid,
  ...numberInputProps
}: PricingNumberInputProps) => {
  // We want to change the opacity of the increment-decrement buttons depending on if we are "using" (focused on / hovering over) this input component
  const [inputWrapperElement, setInputWrapperElement] = useState<HTMLLabelElement | null>(null);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);

  // I need to listen to the enter and leave events on the input's wrapper, only doable via the wrapperRef
  useEffect(() => {
    if (!inputWrapperElement) {
      return;
    }

    const handleEnter = () => setHovered(true);
    const handleLeave = () => setHovered(false);

    inputWrapperElement.addEventListener('mouseenter', handleEnter);
    inputWrapperElement.addEventListener('mouseleave', handleLeave);

    return () => {
      inputWrapperElement.removeEventListener('mouseenter', handleEnter);
      inputWrapperElement.removeEventListener('mouseleave', handleLeave);
    };
  }, [inputWrapperElement]);

  const renderInLowOpacity = !hovered && !focused;

  return (
    <StyledPricingNumberInput
      prefix={
        <HStack gap="spacing4" opacity={renderInLowOpacity ? '20%' : '100%'} transition="opacity 200ms">
          {showIncrementDecrementButtons && (
            <InputIncrementDecrementButtons
              field={field}
              disabled={incrementDecrementButtonsDisabled}
              onIncrement={onIncrement}
              onDecrement={onDecrement}
              onZero={onZero}
            />
          )}
          {tooltip && (
            <Tooltip tooltip={tooltip}>
              <Icon
                icon={IconName.ExclamationSolid}
                variant={iconVariant}
                data-testid="pricing-panel-input-warning-icon"
                tabIndex={-1}
              />
            </Tooltip>
          )}
        </HStack>
      }
      value={field.displayValue}
      disabled={field.isDisabled}
      invalid={invalid ?? !!field.errorString}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      wrapperRef={setInputWrapperElement}
      {...numberInputProps}
    />
  );
};

// Very bespoke class override of our input's placeholder coloring. We wanted them a bit brighter in this pricing panel for showing the previews.
const StyledPricingNumberInput = styled(NumberInput)`
  input::placeholder {
    color: ${({ theme }) => theme.colorTextSubtle};
  }
`;

interface InputIncrementDecrementButtonsProps {
  field: NumericField;
  disabled?: boolean;
  onIncrement?: () => void;
  onDecrement?: () => void;
  onZero?: () => void;
}

const InputIncrementDecrementButtons = ({
  field,
  disabled = false,
  onIncrement,
  onDecrement,
  onZero,
}: InputIncrementDecrementButtonsProps) => {
  return (
    <HStack gap="spacing2" pl="spacing2">
      <IconButton
        icon={IconName.Minus}
        size={FormControlSizes.Tiny}
        disabled={field.isDisabled || disabled || !onDecrement}
        dim
        tabIndex={-1}
        onClick={onDecrement}
        data-testid="pricing-input-decrement-button"
      />
      {onZero && (
        <Button
          size={FormControlSizes.Tiny}
          disabled={field.isDisabled || disabled}
          dim
          tabIndex={-1}
          onClick={onZero}
          data-testid="pricing-input-zero-button"
        >
          0
        </Button>
      )}
      <IconButton
        icon={IconName.Plus}
        size={FormControlSizes.Tiny}
        disabled={field.isDisabled || disabled || !onIncrement}
        dim
        tabIndex={-1}
        onClick={onIncrement}
        data-testid="pricing-input-increment-button"
      />
    </HStack>
  );
};
